/* eslint-disable react/no-array-index-key */
/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';

import classnames from 'classnames';
import Input from 'components/forms/Input';
import { IconCross, IconTick } from 'components/Icons';
import PropTypes from 'prop-types';

import styles from './NewPasswordInput.module.scss';

const NewPasswordInput = ({
  dirtyFields,
  errors,
  ignoreValidationWhenEmpty,
  isRequired,
  label,
  name,
  onValidate,
  placeholder,
  readOnly,
  register,
  ...rest
}) => {
  const [showPassword, toggleShowPassord] = useState(false);
  const [strengthScore, setStrengthScore] = useState(0);
  const [passwordFeedback, setPasswordFeedback] = useState({});
  const [passwordLength, setPasswordLength] = useState(false);
  const [passwordNumbers, setPasswordNumbers] = useState(false);
  const [passwordAlpha, setPasswordAlpha] = useState(false);

  const togglePasswordVisibility = () => {
    toggleShowPassord(!showPassword);
  };

  // eslint-disable-next-line react/prop-types
  const TextWithValidation = ({ children, isValid }) => {
    const Icon = isValid ? IconTick : IconCross;
    const colour = isValid ? 'success' : 'error';

    const messageClass = classnames({
      [styles.warning]: !isValid,
      [styles.strong]: isValid
    });

    return (
      <li className={messageClass}>
        <Icon
          size="32px"
          style={{ 'vertical-align': 'middle', color: `var(--${colour})` }}
        />
        <span>{children}</span>
      </li>
    );
  };

  const getStrengthMessage = () => {
    switch (strengthScore) {
      case 2:
        return 'Medium';
      case 3:
        return 'Strong';
      default:
        return 'Weak';
    }
  };

  const isErrored = () => strengthScore < 3;

  const renderSubtext = () => {
    if (!readOnly && (!ignoreValidationWhenEmpty || dirtyFields?.newPassword)) {
      const strengthMessageClass = classnames(styles['top-warning'], {
        [styles.warning]: isErrored(),
        [styles.strong]: !isErrored()
      });

      return (
        <>
          <span className={strengthMessageClass}>
            Strength:
            <span>{getStrengthMessage()}</span>
          </span>
          <div className={styles['validation-container']}>
            <p className={styles['validation-header']}>
              Your password must contain:
            </p>
            <ul className={styles['validation-list']}>
              <TextWithValidation isValid={passwordLength}>
                At least 8 characters
              </TextWithValidation>
              <TextWithValidation isValid={passwordNumbers}>
                At least 1 number
              </TextWithValidation>
              {/* eslint-disable-next-line max-len */}
              <TextWithValidation isValid={passwordAlpha}>
                At least 1 non alphanumeric character
              </TextWithValidation>
            </ul>
          </div>
        </>
      );
    }
  };

  const validateStrength = (password) => {
    let criteriaMatched = 0;
    let hasLength = false;
    let hasNumbers = false;
    let hasAlpha = false;

    if (password.length > 0) {
      if (password.length >= 8) {
        criteriaMatched += 1;
        hasLength = true;
      }

      if (password.search(/[0-9]/) !== -1) {
        criteriaMatched += 1;
        hasNumbers = true;
      }

      if (password.search(/[^a-zA-Z\d\s:]/i) !== -1) {
        criteriaMatched += 1;
        hasAlpha = true;
      }
    }

    setStrengthScore(criteriaMatched);
    setPasswordLength(hasLength);
    setPasswordNumbers(hasNumbers);
    setPasswordAlpha(hasAlpha);

    if (typeof onValidate === 'function') {
      onValidate(criteriaMatched);
    }
  };

  let suffixIconColor = '';
  if (dirtyFields?.newPassword && !readOnly) {
    suffixIconColor = isErrored() ? 'error' : 'success';
  }
  return (
    <Input
      autoComplete="new-password"
      isDirty={dirtyFields?.newPassword}
      suffixIcon={{
        iconName: showPassword ? 'EyeOff' : 'Eye',
        clickHandler: togglePasswordVisibility,
        toolTip: showPassword ? 'Hide Password' : 'Show password',
        colour: suffixIconColor
      }}
      label={`${label}${isRequired && !label?.endsWith('*') ? '*' : ''}`}
      name={name}
      placeholder={placeholder}
      error={isErrored()}
      type={showPassword ? 'text' : 'password'}
      hideValidationIcon
      inputRef={register({
        required: 'This field is required',
        validate: {
          strength: validateStrength
        }
      })}
      customSubtext={renderSubtext()}
      readOnly={readOnly}
      {...rest}
    />
  );
};

NewPasswordInput.propTypes = {
  dirtyFields: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  ignoreValidationWhenEmpty: PropTypes.bool,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  register: PropTypes.func.isRequired
};

NewPasswordInput.defaultProps = {
  ignoreValidationWhenEmpty: false,
  isRequired: true,
  label: 'Create a password',
  name: 'newPassword',
  placeholder: null
};

export default NewPasswordInput;
